'use strict';

import { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import useMealTypeManager from '../../hooks/useMealTypeManager';

/**
 * Context for meal type state management
 */
const MealTypeContext = createContext(null);

/**
 * Hook to consume meal type context
 * @returns {Object} Meal type state and actions
 */
export function useMealType() {
    const context = useContext(MealTypeContext);

    if (!context) {
        throw new Error('useMealType must be used within a MealTypeProvider');
    }

    return context;
}

/**
 * Provider component for meal type state management
 * Wraps children with meal type context
 */
function MealTypeProvider({
    children,
    preferences,
    plans,
    autoTimer = true,
    timerInterval = 15 * 60 * 1000, // 15 minutes
    onMealTypeChange,
    debugMode = false
}) {
    // MealTypeProvider Component render

    const mealTypeState = useMealTypeManager({
        preferences,
        plans,
        autoTimer,
        timerInterval,
        onMealTypeChange,
        debugMode
    });

    // Don't render children until we have loaded data
    // This prevents the "Dinner" fallback issue
    if (mealTypeState.isLoading) {
        // Still loading, not rendering children
        return null; // Or could return a loading spinner
    }

    // Data loaded, rendering children with meal type

    return (
        <MealTypeContext.Provider value={mealTypeState}>
            {children}
        </MealTypeContext.Provider>
    );
}

MealTypeProvider.propTypes = {
    children: PropTypes.node.isRequired,
    preferences: PropTypes.object,
    plans: PropTypes.array,
    autoTimer: PropTypes.bool,
    timerInterval: PropTypes.number,
    onMealTypeChange: PropTypes.func,
    debugMode: PropTypes.bool
};

export default MealTypeProvider;

/**
 * Higher-order component for injecting meal type state as props
 * Useful for migrating class components gradually
 */
export function withMealType(WrappedComponent) {
    function MealTypeWrappedComponent(props) {
        const mealTypeState = useMealType();

        return (
            <WrappedComponent
                {...props}
                mealTypeState={mealTypeState}
            />
        );
    }

    MealTypeWrappedComponent.displayName = `withMealType(${WrappedComponent.displayName || WrappedComponent.name})`;

    return MealTypeWrappedComponent;
}

/**
 * Hook for components that only need to read meal type state
 * Returns a stable object that won't cause unnecessary re-renders
 */
export function useMealTypeValue() {
    const context = useMealType();

    return {
        currentMealType: context.currentMealType,
        activeMealType: context.activeMealType,
        isManualSelection: context.isManualSelection,
        isNavigating: context.isNavigating,
        lastUpdated: context.lastUpdated
    };
}

/**
 * Hook for components that only need meal type actions
 * Returns a stable object that won't cause unnecessary re-renders
 */
export function useMealTypeActions() {
    const context = useMealType();

    return {
        setMealType: context.setMealType,
        clearManualSelection: context.clearManualSelection,
        navigateToMealType: context.navigateToMealType,
        navigateNext: context.navigateNext,
        navigatePrevious: context.navigatePrevious,
        syncNavigationToCurrent: context.syncNavigationToCurrent
    };
}

/**
 * Hook for navigation-specific functionality
 * Returns navigation state and actions
 */
export function useMealTypeNavigation() {
    const context = useMealType();

    return {
        activeMealType: context.activeMealType,
        isNavigating: context.isNavigating,
        shouldNavigateToToday: context.shouldNavigateToToday,
        canNavigateNext: context.canNavigateNext,
        canNavigatePrevious: context.canNavigatePrevious,
        navigateNext: context.navigateNext,
        navigatePrevious: context.navigatePrevious,
        navigateToMealType: context.navigateToMealType,
        syncNavigationToCurrent: context.syncNavigationToCurrent,
        clearNavigateToToday: context.clearNavigateToToday
    };
}